/**
 * Help Category Configuration
 *
 * Groups the individual help sections (see `helpSections.ts`) into a small set of
 * task-based categories for the Help Center landing page. Each category can be
 * gated by user type, mirroring the `allowedUserTypes` pattern in
 * `navigationConfig.ts`. Categories without `allowedUserTypes` are visible to all.
 *
 * NOTE: This only controls help *discovery* (which category cards a role sees on
 * the Help page). It is not a security boundary — the section content components
 * are unchanged and all remain reachable via direct hash links.
 */
import type { UserType } from '@/types'
import { HELP_SECTIONS, type HelpSection } from './helpSections'

export interface HelpCategory {
  id: string
  label: string
  /** lucide-react icon name; resolved via the icon map in Help.tsx */
  icon: string
  description: string
  /** Section ids (from HELP_SECTIONS) that belong to this category, in display order. */
  sectionIds: string[]
  /** When omitted, the category is visible to every user type. */
  allowedUserTypes?: UserType[]
}

export const HELP_CATEGORIES: HelpCategory[] = [
  {
    id: 'getting-started',
    label: 'Getting Started',
    icon: 'Rocket',
    description: 'New to Grackle CRM? Start here for the basics, shortcuts, and daily tips.',
    sectionIds: ['getting-started', 'quick-start', 'common-tasks', 'keyboard-shortcuts', 'tips'],
  },
  {
    id: 'customers-pipeline',
    label: 'Customers & Pipeline',
    icon: 'Users',
    description: 'Accounts, contacts, leads, opportunities, and the lead-to-close workflow.',
    sectionIds: ['crm', 'sales-workflows'],
    allowedUserTypes: ['Super Admin', 'Admin', 'User', 'Marketing', 'Orders', 'Quotes', 'RFP'],
  },
  {
    id: 'sales-quotes',
    label: 'Sales & Quotes',
    icon: 'ShoppingCart',
    description: 'Quotes, orders, products, pricebooks, and generating quote PDFs.',
    sectionIds: ['sales', 'contract-reviews'],
    allowedUserTypes: ['Super Admin', 'Admin', 'User', 'Orders', 'Quotes', 'RFP'],
  },
  {
    id: 'activities',
    label: 'Activities & Email',
    icon: 'CheckSquare',
    description: 'Tasks, events, calendar, email, and call logging.',
    sectionIds: ['activities'],
    allowedUserTypes: ['Super Admin', 'Admin', 'User', 'Marketing', 'Orders', 'Quotes', 'RFP'],
  },
  {
    id: 'support-tickets',
    label: 'Support & Tickets',
    icon: 'Ticket',
    description: 'Create, manage, and resolve support tickets and internal requests.',
    sectionIds: ['ticketing'],
  },
  {
    id: 'analytics',
    label: 'Reports & Analytics',
    icon: 'BarChart3',
    description:
      'Your dashboard, pre-built reports, the report builder, predictive scores, and invoiced sales.',
    sectionIds: ['dashboard', 'analytics', 'sales-trackers'],
    allowedUserTypes: ['Super Admin', 'Admin', 'User', 'Marketing', 'Orders', 'Quotes', 'RFP'],
  },
  {
    id: 'content',
    label: 'Content & Files',
    icon: 'FolderOpen',
    description: 'Document management and file storage.',
    sectionIds: ['content'],
    allowedUserTypes: ['Super Admin', 'Admin', 'User', 'Marketing', 'Orders', 'Quotes'],
  },
  {
    id: 'integrations',
    label: 'Integrations',
    icon: 'TrendingUp',
    description: 'Access historical Pipedrive data and other integrations.',
    sectionIds: ['pipedrive'],
    allowedUserTypes: ['Super Admin', 'Admin'],
  },
  {
    id: 'administration',
    label: 'Administration',
    icon: 'Settings',
    description: 'User management, impersonation, settings, picklists, and integrations setup.',
    sectionIds: ['administration'],
    allowedUserTypes: ['Super Admin', 'Admin'],
  },
  {
    id: 'faq',
    label: 'FAQ & Troubleshooting',
    icon: 'MessageCircleQuestion',
    description: 'Answers to common questions and fixes for frequent issues.',
    sectionIds: ['faq'],
  },
]

/**
 * Categories visible to a given user type (role-aware Help landing).
 */
export function getVisibleCategories(userType: UserType): HelpCategory[] {
  return HELP_CATEGORIES.filter(
    (cat) => !cat.allowedUserTypes || cat.allowedUserTypes.includes(userType)
  )
}

/**
 * Find the category that contains a given section id. Used to resolve
 * backward-compatible `/help#<sectionId>` deep links to a category view.
 */
export function findCategoryForSection(sectionId: string): HelpCategory | undefined {
  return HELP_CATEGORIES.find((cat) => cat.sectionIds.includes(sectionId))
}

/**
 * Resolve a category's section ids to their full HelpSection metadata, preserving
 * the category's declared order and skipping any unknown ids.
 */
export function getCategorySections(category: HelpCategory): HelpSection[] {
  return category.sectionIds
    .map((id) => HELP_SECTIONS.find((s) => s.id === id))
    .filter((s): s is HelpSection => Boolean(s))
}
